import { StatePage } from "@/components/state-page";
import { filterKeys } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions } from "@/utils/permissions";
import { Button, Select, useModalsStack } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AdminFinance } from "@workspace/admin-domain/src/features/finance/schema";
import { AdminWithdrawal } from "@workspace/admin-domain/src/features/withdrawal/schema";
import { Reservation } from "@workspace/domain/src/features/reservation";
import { Withdrawal } from "@workspace/domain/src/features/withdrawal";
import dayjs from "@workspace/domain/src/utils/dayjs";
import { t } from "i18next";
import { LucideBanknote, LucideCalendar, LucideDownload } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { BreakdownsSection } from "./-breakdowns";
import { KPIsSection } from "./-kpis";
import { LedgerSection } from "./-ledger";
import { TrendsSection } from "./-trends";
import { DEFAULT_LEDGER_PARAMS, DEFAULT_WITHDRAWAL_PARAMS } from "./-utils";
import WithdrawForm from "./-withdraw-form";
import { WithdrawalsSection } from "./-withdrawals";

type TDateControlValue = "thisMonth" | "lastThreeMonths" | "thisYear" | "allTime" | "custom";

// Finance search schema - extends DateRange with sorting for ledger and withdrawals
export const FinanceSearchSchema = z.object({
  // Date range
  startDate: z
    .union([z.string().transform((date) => dayjs(date).toDate()), z.date()])
    .optional()
    .default(dayjs().subtract(30, "day").startOf("day").toDate()),
  endDate: z
    .union([z.string().transform((date) => dayjs(date).toDate()), z.date()])
    .optional()
    .default(dayjs().endOf("day").toDate()),

  // Ledger
  ledgerPage: AdminFinance.PaymentsFilters.shape.page.optional().default(DEFAULT_LEDGER_PARAMS.page),
  ledgerLimit: AdminFinance.PaymentsFilters.shape.limit.optional().default(DEFAULT_LEDGER_PARAMS.limit),
  ledgerSortBy: AdminFinance.PaymentsFilters.shape.sortBy.optional().default(DEFAULT_LEDGER_PARAMS.sortBy),
  ledgerSortOrder: AdminFinance.PaymentsFilters.shape.sortOrder.optional().default(DEFAULT_LEDGER_PARAMS.sortOrder),
  ledgerStatus: z.enum(Reservation.PaymentStatus).optional(),
  ledgerSearch: z.string().optional(),

  // Withdrawal
  withdrawalPage: AdminWithdrawal.GetAll.shape.page.optional().default(DEFAULT_WITHDRAWAL_PARAMS.page),
  withdrawalLimit: AdminWithdrawal.GetAll.shape.limit.optional().default(DEFAULT_WITHDRAWAL_PARAMS.limit),
  withdrawalSortBy: AdminWithdrawal.GetAll.shape.sortBy.optional().default(DEFAULT_WITHDRAWAL_PARAMS.sortBy),
  withdrawalSortOrder: AdminWithdrawal.GetAll.shape.sortOrder.optional().default(DEFAULT_WITHDRAWAL_PARAMS.sortOrder),
  withdrawalStatus: z.enum(Withdrawal.WithdrawalStatus).optional(),

  // Withdrawal highlighting
  withdrawalId: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/finances/")({
  component: Component,
  validateSearch: FinanceSearchSchema,
  beforeLoad: requirePermissions(true),
  loaderDeps: ({ search }) => filterKeys(search, FinanceSearchSchema.shape),
  loader: async ({ context, deps }) => {
    const {
      startDate,
      endDate,
      ledgerSortBy,
      ledgerSortOrder,
      ledgerStatus,
      ledgerSearch,
      withdrawalSortBy,
      withdrawalSortOrder,
      withdrawalStatus,
    } = deps;
    void Promise.all([
      // KPIs
      context.queryClient.prefetchQuery(orpc.finance.getTotalHeld.queryOptions()),
      context.queryClient.prefetchQuery(orpc.finance.getTotalDue.queryOptions()),
      context.queryClient.prefetchQuery(orpc.withdrawal.getAvailableBalance.queryOptions()),
      context.queryClient.prefetchQuery(orpc.withdrawal.getMethod.queryOptions()),
      context.queryClient.prefetchQuery(
        orpc.finance.getTotalRevenue.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.finance.getTotalRefunds.queryOptions({
          input: { startDate, endDate },
        }),
      ),

      // Trends
      context.queryClient.prefetchQuery(
        orpc.finance.getPaymentsOverTime.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.finance.getRefundsOverTime.queryOptions({
          input: { startDate, endDate },
        }),
      ),

      // Breakdowns
      context.queryClient.prefetchQuery(
        orpc.finance.getPaymentMethodBreakdown.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.finance.getPaymentStatusBreakdown.queryOptions({
          input: { startDate, endDate },
        }),
      ),

      // Ledger
      context.queryClient.prefetchQuery(
        orpc.finance.getPayments.queryOptions({
          input: {
            startDate,
            endDate,
            status: ledgerStatus,
            search: ledgerSearch,
            page: DEFAULT_LEDGER_PARAMS.page,
            limit: DEFAULT_LEDGER_PARAMS.limit,
            sortBy: ledgerSortBy ?? DEFAULT_LEDGER_PARAMS.sortBy,
            sortOrder: ledgerSortOrder ?? DEFAULT_LEDGER_PARAMS.sortOrder,
          },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.finance.countPayments.queryOptions({
          input: {
            startDate,
            endDate,
            status: ledgerStatus,
            search: ledgerSearch,
          },
        }),
      ),

      // Withdrawals
      context.queryClient.prefetchQuery(
        orpc.withdrawal.getAll.queryOptions({
          input: {
            startDate,
            endDate,
            status: withdrawalStatus,
            page: DEFAULT_WITHDRAWAL_PARAMS.page,
            limit: DEFAULT_WITHDRAWAL_PARAMS.limit,
            sortBy: withdrawalSortBy ?? DEFAULT_WITHDRAWAL_PARAMS.sortBy,
            sortOrder: withdrawalSortOrder ?? DEFAULT_WITHDRAWAL_PARAMS.sortOrder,
          },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.withdrawal.count.queryOptions({
          input: {
            startDate,
            endDate,
            status: withdrawalStatus,
          },
        }),
      ),
    ]);
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="finance" emptyResourceType={undefined} />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="finance" emptyResourceType={undefined} />,
});

const DEFAULT_DATE_RANGES = {
  thisMonth: {
    startDate: dayjs().subtract(30, "day").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate(),
  },
  lastThreeMonths: {
    startDate: dayjs().subtract(90, "day").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate(),
  },
  thisYear: {
    startDate: dayjs().subtract(1, "year").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate(),
  },
  allTime: {
    startDate: dayjs("1970-01-01").toDate(),
    endDate: dayjs().endOf("day").toDate(),
  },
} as const;

function rangeToDateControlValue(startDate: Date, endDate: Date) {
  if (
    startDate?.getTime() === DEFAULT_DATE_RANGES.thisMonth.startDate?.getTime() &&
    endDate?.getTime() === DEFAULT_DATE_RANGES.thisMonth.endDate?.getTime()
  ) {
    return "thisMonth";
  }
  if (
    startDate?.getTime() === DEFAULT_DATE_RANGES.lastThreeMonths.startDate?.getTime() &&
    endDate?.getTime() === DEFAULT_DATE_RANGES.lastThreeMonths.endDate?.getTime()
  ) {
    return "lastThreeMonths";
  }
  if (
    startDate?.getTime() === DEFAULT_DATE_RANGES.thisYear.startDate?.getTime() &&
    endDate?.getTime() === DEFAULT_DATE_RANGES.thisYear.endDate?.getTime()
  ) {
    return "thisYear";
  }
  if (
    startDate?.getTime() === DEFAULT_DATE_RANGES.allTime.startDate?.getTime() &&
    endDate?.getTime() === DEFAULT_DATE_RANGES.allTime.endDate?.getTime()
  ) {
    return "allTime";
  }
  return "custom";
}

function dateControlValueToRange(dateControlValue: TDateControlValue) {
  switch (dateControlValue) {
    case "thisMonth":
      return {
        startDate: DEFAULT_DATE_RANGES.thisMonth.startDate,
        endDate: DEFAULT_DATE_RANGES.thisMonth.endDate,
      };
    case "lastThreeMonths":
      return {
        startDate: DEFAULT_DATE_RANGES.lastThreeMonths.startDate,
        endDate: DEFAULT_DATE_RANGES.lastThreeMonths.endDate,
      };
    case "thisYear":
      return {
        startDate: DEFAULT_DATE_RANGES.thisYear.startDate,
        endDate: DEFAULT_DATE_RANGES.thisYear.endDate,
      };
    case "allTime":
      return {
        startDate: DEFAULT_DATE_RANGES.allTime.startDate,
        endDate: DEFAULT_DATE_RANGES.allTime.endDate,
      };
    case "custom":
      return {
        startDate: undefined,
        endDate: undefined,
      };
  }
}

function Component() {
  const { startDate, endDate } = Route.useSearch();

  return (
    <div className="gap-md md:gap-lg flex grow flex-col">
      <FinanceHeader />

      <KPIsSection startDate={startDate} endDate={endDate} />

      <div className="gap-md md:gap-lg grid grid-cols-1 md:grid-cols-5 lg:grid-cols-3">
        <div className="md:col-span-3 lg:col-span-2">
          <TrendsSection startDate={startDate} endDate={endDate} />
        </div>
        <div className="md:col-span-2 lg:col-span-1">
          <BreakdownsSection startDate={startDate} endDate={endDate} />
        </div>
      </div>

      <LedgerSection />
      <WithdrawalsSection />
    </div>
  );
}

function FinanceHeader() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const [dateControlValue, setDateControlValue] = useState<TDateControlValue>(() =>
    rangeToDateControlValue(search.startDate, search.endDate),
  );

  const [localStartDate, setLocalStartDate] = useState<Date | undefined>(search.startDate);
  const [localEndDate, setLocalEndDate] = useState<Date | undefined>(search.endDate);

  // Withdraw modal stack
  const withdrawStack = useModalsStack(["withdraw", "confirm"]);

  // Fetch available balance for withdraw
  const { data: availableBalance } = useQuery(orpc.withdrawal.getAvailableBalance.queryOptions());

  // Withdraw mutation
  const withdrawMutation = useMutation(
    orpc.withdrawal.withdraw.mutationOptions({
      onSuccess: () => {
        notifications.show({
          title: t("finance.withdraw.success.title"),
          message: t("finance.withdraw.success.description"),
          color: "green",
        });
        withdrawStack.closeAll();
        // Invalidate the available balance query to refresh the data
        void queryClient.invalidateQueries({
          queryKey: orpc.withdrawal.getAvailableBalance.queryOptions().queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.withdrawal.getMethod.queryOptions().queryKey,
        });
      },
      onError: (error) => {
        notifications.show({
          title: t("error.title"),
          message: error instanceof Error ? error.message : t("finance.withdraw.error.description"),
          color: "red",
        });
      },
    }),
  );

  const exportMutation = useMutation({
    mutationFn: async () => {
      const [{ pdf }, { FinancePDFDocument }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./-pdf-document"),
      ]);

      // Fetch agency data
      const agency = await queryClient.fetchQuery(orpc.agency.get.queryOptions());

      // Fetch all payments with pagination
      const allPayments: AdminFinance["PaymentEntry"][] = [];
      let page = 0;
      const limit = 1000;
      let hasMore = true;

      while (hasMore) {
        const payments = await queryClient.fetchQuery(
          orpc.finance.getPayments.queryOptions({
            input: {
              startDate: search.startDate,
              endDate: search.endDate,
              page,
              limit,
              sortBy: "createdAt",
              sortOrder: "desc",
            },
          }),
        );

        allPayments.push(...payments);

        if (payments.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      }

      // Fetch KPI data
      const [heldAmount, amountDue, totalRevenue, totalRefunded] = await Promise.all([
        queryClient.fetchQuery(orpc.finance.getTotalHeld.queryOptions()),
        queryClient.fetchQuery(orpc.finance.getTotalDue.queryOptions()),
        queryClient.fetchQuery(
          orpc.finance.getTotalRevenue.queryOptions({
            input: {
              startDate: search.startDate,
              endDate: search.endDate,
            },
          }),
        ),
        queryClient.fetchQuery(
          orpc.finance.getTotalRefunds.queryOptions({
            input: {
              startDate: search.startDate,
              endDate: search.endDate,
            },
          }),
        ),
      ]);

      // Generate PDF
      const doc = (
        <FinancePDFDocument
          agency={agency}
          payments={allPayments}
          kpis={{
            heldAmount,
            amountDue,
            totalRevenue,
            totalRefunded,
          }}
          dateRange={{
            startDate: search.startDate,
            endDate: search.endDate,
          }}
          baseUrl={window.location.origin}
        />
      );

      const blob = await pdf(doc).toBlob();

      // Download the PDF
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = dayjs().format("YYYY-MM-DD");
      link.download = `finance-report-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      notifications.show({
        title: t("success"),
        message: t("finance.exportPDF.success.description"),
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: t("error.title"),
        message: error instanceof Error ? error.message : t("finance.exportPDF.error.description"),
        color: "red",
      });
    },
  });

  return (
    <div className="gap-md py-md flex flex-col">
      <div className="gap-md flex flex-col md:flex-row md:items-center md:justify-between">
        <p className="text-2xl font-semibold md:text-3xl">{t("finance.title")}</p>
        <div className="gap-sm flex flex-col-reverse items-center md:flex-row">
          <div className="gap-sm flex w-full md:w-auto">
            {/* Withdraw button */}
            <Button
              className="grow md:grow-0"
              variant="filled"
              color="green"
              leftSection={<LucideBanknote className="size-4" />}
              onClick={() => withdrawStack.open("withdraw")}
              disabled={CONSTANTS.DEMO || !availableBalance || availableBalance <= 0}
            >
              {t("finance.withdraw.button")}
            </Button>

            {/* Export button */}
            <Button
              className="grow md:grow-0"
              variant="default"
              leftSection={<LucideDownload className="size-4" />}
              onClick={() => exportMutation.mutate()}
              disabled={CONSTANTS.DEMO}
              loading={exportMutation.isPending}
            >
              {t("finance.export")}
            </Button>
          </div>

          {/* Custom date range picker */}
          {dateControlValue === "custom" ? (
            <DatePickerInput
              type="range"
              placeholder={t("analytics.dateRange.customRange")}
              value={[localStartDate ?? null, localEndDate ?? null]}
              onChange={(value) => {
                const newStartDate = value[0] ? dayjs(value[0]).toDate() : undefined;
                const newEndDate = value[1] ? dayjs(value[1]).toDate() : undefined;

                setLocalStartDate(newStartDate);
                setLocalEndDate(newEndDate);

                if (!newStartDate && !newEndDate) {
                  setDateControlValue("allTime");
                }

                if (!(newStartDate && !newEndDate)) {
                  void navigate({
                    search: {
                      startDate: newStartDate,
                      endDate: newEndDate,
                    },
                  });
                }
              }}
              leftSection={<LucideCalendar className="size-4" />}
              className="w-full md:w-auto"
              maxDate={dayjs().toDate()}
              clearable
            />
          ) : null}

          {/* Date range selector */}
          <Select
            unselectable="on"
            className="w-full md:w-40"
            value={dateControlValue}
            onChange={(value) => {
              if (!value) return;

              const newValue = value as TDateControlValue;
              setDateControlValue(newValue);

              if (newValue !== "custom") {
                void navigate({
                  search: dateControlValueToRange(newValue),
                });
              }
            }}
            data={[
              {
                label: t("analytics.dateRange.thisMonth"),
                value: "thisMonth",
              },
              {
                label: t("analytics.dateRange.lastThreeMonths"),
                value: "lastThreeMonths",
              },
              {
                label: t("analytics.dateRange.thisYear"),
                value: "thisYear",
              },
              {
                label: t("analytics.dateRange.allTime"),
                value: "allTime",
              },
              {
                label: t("analytics.dateRange.custom"),
                value: "custom",
              },
            ]}
          />
        </div>
      </div>

      {/* Withdraw Modal */}
      {availableBalance !== undefined ? (
        <WithdrawForm
          stack={withdrawStack}
          onSubmit={async ({ payload }) => {
            await withdrawMutation.mutateAsync(payload);
          }}
        />
      ) : null}
    </div>
  );
}
